import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, LogOut, Download, RefreshCw, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "scheduled", "completed", "cancelled"];
const STATUS_STYLES = {
    new: "bg-blue-100 text-blue-800",
    contacted: "bg-amber-100 text-amber-800",
    scheduled: "bg-purple-100 text-purple-800",
    completed: "bg-green-100 text-green-800",
    cancelled: "bg-gray-200 text-gray-500",
};

export default function Admin() {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [bookings, setBookings] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((r) => setUser(r.data))
            .catch(() => setUser(false));
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/bookings`, {
                withCredentials: true,
                params: filter !== "all" ? { status: filter } : {},
            });
            setBookings(r.data);
        } catch {
            toast.error("Failed to load bookings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filter]);

    const login = async (e) => {
        e.preventDefault();
        try {
            const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            setUser(r.data);
            toast.success("Welcome back");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed");
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        setUser(false);
    };

    const setStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/bookings/${id}`, { status }, { withCredentials: true });
            setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Status update failed");
        }
    };

    const exportCsv = () => {
        const rows = [
            ["Date", "Name", "Phone", "Area/PIN", "Appliance", "Issue", "Visit date", "Preferred time", "Status"],
            ...bookings.map((b) => [
                new Date(b.created_at).toLocaleString(),
                b.full_name,
                b.phone,
                b.area_pin,
                b.appliance_type,
                (b.issue_description || "").replace(/\n/g, " "),
                b.preferred_date || "",
                b.preferred_time,
                b.status,
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = "brighten-bookings.csv";
        a.click();
    };

    if (user === null) {
        return (
            <div className="min-h-screen bg-[#FBF7F3] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-[#8D1717] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-[#FBF7F3] flex items-center justify-center px-4">
                <div className="w-full max-w-sm rounded-3xl bg-white border border-[#EADFD8] shadow-xl p-8">
                    <div className="flex flex-col items-center text-center">
                        <img src="/logo-mark.png" alt="Brighten Services crest logo" className="h-16 w-auto" />
                        <h1 className="font-display text-2xl font-black text-[#2A0505] mt-4">Admin Login</h1>
                        <p className="text-sm text-[#6B5B57] mt-1">Brighten Services booking dashboard</p>
                    </div>
                    <form onSubmit={login} data-testid="admin-login-form" className="mt-8 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="admin-email">Email</Label>
                            <Input id="admin-email" data-testid="admin-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" className="h-12 rounded-xl" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="admin-password">Password</Label>
                            <Input id="admin-password" data-testid="admin-login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" className="h-12 rounded-xl" />
                        </div>
                        <button type="submit" data-testid="admin-login-submit-button" className="w-full bg-[#8D1717] hover:bg-[#6D1010] text-white font-bold rounded-full py-3.5 transition-colors">
                            Sign In
                        </button>
                    </form>
                    <a href="/" data-testid="admin-back-to-site" className="block text-center text-xs text-[#a08b85] hover:text-[#8D1717] mt-6 transition-colors">
                        Back to website
                    </a>
                </div>
            </div>
        );
    }

    const today = new Date().toDateString();
    const stats = [
        { label: "Total requests", value: bookings.length, testid: "admin-stat-total" },
        { label: "New", value: bookings.filter((b) => b.status === "new").length, testid: "admin-stat-new" },
        { label: "Today", value: bookings.filter((b) => new Date(b.created_at).toDateString() === today).length, testid: "admin-stat-today" },
    ];

    return (
        <div className="min-h-screen bg-[#FBF7F3]">
            <header className="bg-[#2A0505] text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo-mark.png" alt="" className="h-9 w-auto bg-[#FBF7F3] rounded-lg px-1.5 py-1" />
                        <span className="font-display font-black tracking-tight">Brighten Admin</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/" data-testid="admin-view-site" className="text-xs text-white/70 hover:text-white transition-colors">View site</a>
                        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors">
                            <LogOut className="w-3.5 h-3.5" />Logout
                        </button>
                    </div>
                </div>
            </header>

            <main data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-3 gap-4">
                    {stats.map((s) => (
                        <div key={s.testid} data-testid={s.testid} className="rounded-2xl bg-white border border-[#EADFD8] p-5">
                            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#a08b85]">{s.label}</p>
                            <p className="font-display text-3xl font-black text-[#2A0505] mt-1">{s.value}</p>
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-8">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger data-testid="admin-status-filter" className="w-44 h-11 rounded-xl bg-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`admin-filter-${s}`}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <button onClick={load} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 text-sm font-semibold border border-[#EADFD8] bg-white rounded-full px-5 py-2.5 hover:border-[#8D1717] hover:text-[#8D1717] transition-colors">
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Refresh
                    </button>
                    <button onClick={exportCsv} data-testid="admin-export-csv-button" className="inline-flex items-center gap-2 text-sm font-semibold border border-[#EADFD8] bg-white rounded-full px-5 py-2.5 hover:border-[#8D1717] hover:text-[#8D1717] transition-colors">
                        <Download className="w-4 h-4" />Export CSV
                    </button>
                </div>

                {bookings.length === 0 ? (
                    <div data-testid="admin-empty-state" className="mt-8 rounded-3xl border-2 border-dashed border-[#EADFD8] bg-white p-16 text-center">
                        <Inbox className="w-10 h-10 text-[#a08b85] mx-auto" />
                        <p className="text-sm text-[#6B5B57] mt-4">No booking requests yet.</p>
                    </div>
                ) : (
                    <div className="mt-8 rounded-3xl border border-[#EADFD8] bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                            <table data-testid="admin-bookings-table" className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#a08b85] border-b border-[#EADFD8]">
                                        <th className="px-5 py-4">Date</th>
                                        <th className="px-5 py-4">Customer</th>
                                        <th className="px-5 py-4">Contact</th>
                                        <th className="px-5 py-4">Appliance</th>
                                        <th className="px-5 py-4">Issue</th>
                                        <th className="px-5 py-4">Time</th>
                                        <th className="px-5 py-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.map((b) => (
                                        <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-[#F3E9E2] last:border-0 align-top">
                                            <td className="px-5 py-4 whitespace-nowrap text-[#6B5B57]">{new Date(b.created_at).toLocaleString()}</td>
                                            <td className="px-5 py-4">
                                                <p className="font-semibold text-[#2A0505]">{b.full_name}</p>
                                                <p className="text-xs text-[#a08b85] mt-0.5">{b.area_pin}</p>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex items-center gap-2">
                                                    <a href={`tel:${b.phone}`} data-testid={`booking-call-${b.id}`} className="w-8 h-8 rounded-full bg-[#8D1717]/10 flex items-center justify-center hover:bg-[#8D1717] hover:text-white text-[#8D1717] transition-colors" aria-label="Call">
                                                        <Phone className="w-3.5 h-3.5" />
                                                    </a>
                                                    <a
                                                        href={`https://wa.me/91${b.phone.replace(/\D/g, "").slice(-10)}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        data-testid={`booking-whatsapp-${b.id}`}
                                                        className="w-8 h-8 rounded-full bg-[#22C55E]/10 flex items-center justify-center hover:bg-[#22C55E] hover:text-white text-[#16A34A] transition-colors"
                                                        aria-label="WhatsApp"
                                                    >
                                                        <MessageCircle className="w-3.5 h-3.5" />
                                                    </a>
                                                    <span className="text-xs text-[#6B5B57]">{b.phone}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap font-medium text-[#2A0505]">{b.appliance_type}</td>
                                            <td className="px-5 py-4 text-[#6B5B57] max-w-[220px]">{b.issue_description}</td>
                                            <td className="px-5 py-4 whitespace-nowrap text-[#6B5B57]">{b.preferred_date ? `${b.preferred_date} · ` : ""}{b.preferred_time}</td>
                                            <td className="px-5 py-4">
                                                <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                                                    <SelectTrigger data-testid={`booking-status-${b.id}`} className={`h-9 w-32 rounded-full text-xs font-bold border-0 ${STATUS_STYLES[b.status] || ""}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {STATUSES.map((s) => (
                                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
